import {
  PeopleTableInfo,
  Person,
} from '@aibees/elephant/feature/search-table-people';
import type { Nullable } from '@aibees/shared/definition';
import { useMemo } from 'react';
import { useCompanyVisibilityState } from './use-company-visibility-state';
import { usePersonCompanyInfo } from './use-person-company-info';

export function useDisplayCompanyName(
  personData?: Person | PeopleTableInfo | null,
  fallbackName?: Nullable<string>
): {
  displayName: string;
  hasData: boolean;
  color: 'grey.700' | 'grey.400';
} {
  const {
    isSelfEmployedOrFreelancer,
    employmentType,
    wasPreviouslySelfEmployedOrFreelancer,
    previousEmploymentType,
    companyNameHasData,
  } = useCompanyVisibilityState(personData);
  const { companyName: extractedCompanyName } = usePersonCompanyInfo(
    personData ?? undefined
  );

  return useMemo(() => {
    const normalizedFallback =
      fallbackName && fallbackName.trim() ? fallbackName : null;
    const companyName = extractedCompanyName ?? normalizedFallback ?? 'N/A';

    if (isSelfEmployedOrFreelancer && employmentType) {
      return {
        displayName: employmentType,
        hasData: true,
        color: 'grey.700' as const,
      };
    }

    if (wasPreviouslySelfEmployedOrFreelancer && previousEmploymentType) {
      return {
        displayName: previousEmploymentType,
        hasData: true,
        color: 'grey.700' as const,
      };
    }

    const hasData = companyName === 'N/A' ? false : companyNameHasData;
    return {
      displayName: companyName,
      hasData,
      color: hasData ? 'grey.700' : 'grey.400',
    };
  }, [
    fallbackName,
    isSelfEmployedOrFreelancer,
    employmentType,
    wasPreviouslySelfEmployedOrFreelancer,
    previousEmploymentType,
    companyNameHasData,
    extractedCompanyName,
  ]);
}
